import { useEffect, useId, useRef } from 'react';
import { Platform } from 'react-native';
import { usePathname } from 'expo-router';
import { activateKeepAwakeAsync, deactivateKeepAwake } from 'expo-keep-awake';
import { useUIStore } from '@/store/ui';
import useAppActive from './useAppActive';

/** Missing or invalid preferences never prevent auto-lock. */
export function isKeepScreenAwakeEnabled(keepScreenAwake) {
  return keepScreenAwake === true;
}

/**
 * Opt-in screen wake lock for the visible native chat. Mounted once in the
 * root layout so retained chat screens cannot hold competing locks.
 */
export default function useKeepScreenAwake() {
  const preference = useUIStore(state => state.keepScreenAwakeInChats);
  const pathname = usePathname();
  const appActive = useAppActive();
  const id = useId();
  const activationCount = useRef(0);
  const enabled = (Platform.OS === 'ios' || Platform.OS === 'android')
    && isKeepScreenAwakeEnabled(preference)
    && appActive
    && /^\/c\/[^/]+\/?$/.test(pathname || '');

  useEffect(() => {
    if (!enabled) return undefined;
    // A late activation must not release a newer lock after a quick toggle.
    const tag = `gab-ai-chat:${id}:${++activationCount.current}`;
    let cancelled = false;
    const release = () => deactivateKeepAwake(tag).catch(() => {});
    activateKeepAwakeAsync(tag).then(() => {
      if (cancelled) return release();
    }).catch(() => {});
    return () => {
      cancelled = true;
      void release();
    };
  }, [enabled, id]);
}
